import {batch} from 'react-redux';

import {buildContactsManagerAccountImportErredPayload} from '../../../../../../libs/contacts-manager/src/lib/ui/web/helpers/trackingHelpers';
import {AsyncHandler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AppRoutesPrefixesEnum} from '../../../../../components/appRoutesPrefixes';
import {ActionWithPayload} from '../../../../../helpers/reduxHelpers';
import {CustomFieldModel} from '../../../../../models/api/customFieldModel';
import {HttpResponseError} from '../../../../../services/http/httpError';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {ThunkAction, ThunkAsyncAction} from '../../../../appThunk';
import {
  fetchAccountsCsvMetadataAsyncAction,
  uploadAccountsCSVAsyncAction,
  uploadAccountsCSVAsyncActionV2,
} from '../../../../data/accounts/accountsActions';
import {uploadContactsCsvAsyncAction} from '../../../../data/cards/cardsActions';
import {getCustomFieldsForAccountsFlat} from '../../../../data/customFields/customFieldsSelectors';
import {navigateToLinkAction} from '../../../../stateless/navigationActions';
import {NavigationBlockerId} from '../../../app/appPanelHelpers';
import {ContactsManagerPanelId} from '../../../panelStateBase';
import {
  getContactsManagerPanel,
  makeGetAccountsCSVFileMapping,
  makeGetContactsManagerPanel,
} from '../contactsManagerPanelSelectors';
import {
  ContactsManagerAccountsCsvErrorsEnum,
  ContactsManagerContactsCsvErrorsEnum,
  ContactsManagerCsvErrorsEnum,
  ContactsManagerFileError,
  ContactsManagerFileStateTypesEnum,
  FileMetadata,
} from './contactsManagerFileState';

/*
 * Type enum.
 */

export enum ContactsManagerFileActionsEnum {
  UPLOAD_FILE = '@@panels/contacts-manager/file/UPLOAD_FILE',
  UPLOAD_COMPLETE = '@@panels/contacts-manager/file/UPLOAD_COMPLETE',
  UPLOAD_FAILED = '@@panels/contacts-manager/file/UPLOAD_FAILED',
  CLEAR_FILE = '@@panels/contacts-manager/file/CLEAR_FILE',
  FILE_METADATA_FETCHED = '@@panels/contacts-manager/file/FILE_METADATA_FETCHED',
  SET_MAPPING = '@@panels/contacts-manager/file/SET_MAPPING',
}

/*
 * Types.
 */

interface ContactsManagerFileBasePayload {
  panelId: ContactsManagerPanelId;
}

interface UploadFileActionPayload extends ContactsManagerFileBasePayload {
  type: ContactsManagerFileStateTypesEnum;
  file: File;
}

interface SetMappingActionPayload extends ContactsManagerFileBasePayload {
  mapping: Record<string, number>;
}

interface UploadFileAction extends ActionWithPayload<UploadFileActionPayload> {
  type: ContactsManagerFileActionsEnum.UPLOAD_FILE;
}

interface SetMappingAction extends ActionWithPayload<SetMappingActionPayload> {
  type: ContactsManagerFileActionsEnum.SET_MAPPING;
}

interface ClearFileAction extends ActionWithPayload<ContactsManagerFileBasePayload> {
  type: ContactsManagerFileActionsEnum.CLEAR_FILE;
}

interface UploadCompleteActionPayload extends ContactsManagerFileBasePayload {
  dryRun: boolean;
  isV2Upload?: boolean;
}

interface UploadCompleteAction extends ActionWithPayload<UploadCompleteActionPayload> {
  type: ContactsManagerFileActionsEnum.UPLOAD_COMPLETE;
}

interface AccountCSVUploadFailedActionPayload extends ContactsManagerFileBasePayload {
  fileType: ContactsManagerFileStateTypesEnum;
  errors: ReadonlyArray<ContactsManagerFileError>;
  errorFile?: File;
}

interface AccountCSVFileMetadataFetchedActionPayload extends ContactsManagerFileBasePayload {
  fileMetadata: FileMetadata;
}

interface UploadFailedAction extends ActionWithPayload<AccountCSVUploadFailedActionPayload> {
  type: ContactsManagerFileActionsEnum.UPLOAD_FAILED;
}

interface FileMetadataFetchedAction extends ActionWithPayload<AccountCSVFileMetadataFetchedActionPayload> {
  type: ContactsManagerFileActionsEnum.FILE_METADATA_FETCHED;
}

/*
 * Action creators.
 */

function uploadFile(payload: UploadFileActionPayload): UploadFileAction {
  return {
    type: ContactsManagerFileActionsEnum.UPLOAD_FILE,
    payload,
  };
}

function setMapping(payload: SetMappingActionPayload): SetMappingAction {
  return {
    type: ContactsManagerFileActionsEnum.SET_MAPPING,
    payload,
  };
}

function clearFile(panelId: ContactsManagerPanelId): ClearFileAction {
  return {
    type: ContactsManagerFileActionsEnum.CLEAR_FILE,
    payload: {
      panelId,
    },
  };
}

function uploadComplete(
  panelId: ContactsManagerPanelId,
  dryRun: boolean,
  isV2Upload?: boolean,
): UploadCompleteAction {
  return {
    type: ContactsManagerFileActionsEnum.UPLOAD_COMPLETE,
    payload: {
      panelId,
      dryRun,
      isV2Upload: isV2Upload ?? false,
    },
  };
}

function uploadFailed(payload: AccountCSVUploadFailedActionPayload): UploadFailedAction {
  return {
    type: ContactsManagerFileActionsEnum.UPLOAD_FAILED,
    payload,
  };
}

function fileMetadataFetched(
  panelId: ContactsManagerPanelId,
  fileMetadata: FileMetadata,
): FileMetadataFetchedAction {
  return {
    type: ContactsManagerFileActionsEnum.FILE_METADATA_FETCHED,
    payload: {
      panelId,
      fileMetadata,
    },
  };
}

/*
 * Union type.
 */

export type ContactsManagerFileAction =
  | UploadFileAction
  | UploadCompleteAction
  | ClearFileAction
  | FileMetadataFetchedAction
  | SetMappingAction
  | UploadFailedAction;

/*
 * Actions.
 */

export function contactsManagerUploadAccountsCSVActionV2(
  panelId: ContactsManagerPanelId,
  file: File,
  dryRun: boolean = true,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    dispatch(
      uploadFile({
        file,
        type: ContactsManagerFileStateTypesEnum.ACCOUNTS_CSV,
        panelId,
      }),
    );

    try {
      const mapping = makeGetAccountsCSVFileMapping()(getState());
      await dispatch(uploadAccountsCSVAsyncActionV2(file, dryRun, mapping));
      dispatch(uploadComplete(panelId, dryRun, true));
      if (dryRun) {
        const metadata = await dispatch(fetchAccountsCsvMetadataAsyncAction(file));
        const accountCustomFields = getCustomFieldsForAccountsFlat(getState());
        const newMapping = computeNewMapping(metadata.headers, accountCustomFields, mapping);
        batch(() => {
          dispatch(fileMetadataFetched(panelId, metadata));
          dispatch(contactsManagerSetMappingAction(newMapping));
        });
      }
    } catch (error) {
      error.body?.invalids?.forEach((err: ContactsManagerFileError) =>
        trackInterfaceEvent(
          InterfaceEventsEnum.CONTACTS_MANAGER_ACCOUNT_IMPORT_ERROR,
          buildContactsManagerAccountImportErredPayload(err.error),
        ),
      );
      dispatch(
        uploadFailed({
          panelId,
          fileType: ContactsManagerFileStateTypesEnum.ACCOUNTS_CSV,
          errors: error.body?.invalids,
        }),
      );
    }
  };
}

export function contactsManagerImportAccounts(
  panelId: ContactsManagerPanelId,
  file: File,
  ignoreNavigationBlockerId?: NavigationBlockerId,
): ThunkAction {
  return async (dispatch) => {
    batch(() => {
      dispatch(
        navigateToLinkAction(
          `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}`,
          undefined,
          ignoreNavigationBlockerId,
        ),
      );
      dispatch(contactsManagerUploadAccountsCSVActionV2(panelId, file, false));
    });
  };
}

export function contactsManagerUploadAccountsCSVAction(file: File, dryRun: boolean = true): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(
      uploadFile({
        file,
        type: ContactsManagerFileStateTypesEnum.ACCOUNTS_CSV,
        panelId: contactsManagerPanel.id,
      }),
    );

    // If the file type is not a csv, do not upload and just show an error.
    if (!isValidCSVFileType(file)) {
      trackInterfaceEvent(
        InterfaceEventsEnum.CONTACTS_MANAGER_ACCOUNT_IMPORT_ERROR,
        buildContactsManagerAccountImportErredPayload(ContactsManagerCsvErrorsEnum.INVALID_FILE_TYPE),
      );
      dispatch(
        uploadFailed({
          panelId: contactsManagerPanel.id,
          fileType: ContactsManagerFileStateTypesEnum.ACCOUNTS_CSV,
          errors: [
            {
              error: ContactsManagerCsvErrorsEnum.INVALID_FILE_TYPE,
            },
          ],
        }),
      );
      return;
    }

    try {
      await dispatch(uploadAccountsCSVAsyncAction(file, dryRun));
      dispatch(uploadComplete(contactsManagerPanel.id, dryRun));
    } catch (error) {
      const errors: ReadonlyArray<ContactsManagerFileError> = !error.file
        ? error.body.invalids.map((invalid: any) => {
            // TODO: Remove this when we have more informative backend errors returned
            //       They are doing work to change generic `parse_error` to something more specific
            // Manual mapping as the error type is a generic 'parse_error'
            if (invalid.reason === 'More row columns than header columns') {
              return {
                error: ContactsManagerCsvErrorsEnum.PARSE_ERROR_MORE_ROWS_THAN_HEADER,
              };
            }

            return {
              error: invalid.error,
              row: invalid.row,
            };
          })
        : [
            {
              error: ContactsManagerAccountsCsvErrorsEnum.ROWS_ERRORS_WITH_DOWNLOAD,
            },
          ];
      errors.forEach((err) =>
        trackInterfaceEvent(
          InterfaceEventsEnum.CONTACTS_MANAGER_ACCOUNT_IMPORT_ERROR,
          buildContactsManagerAccountImportErredPayload(err.error),
        ),
      );
      dispatch(
        uploadFailed({
          panelId: contactsManagerPanel.id,
          fileType: ContactsManagerFileStateTypesEnum.ACCOUNTS_CSV,
          errorFile: error.file,
          errors,
        }),
      );
    }
  };
}

export function contactsManagerUploadContactsCsvAsyncAction(
  panelId: ContactsManagerPanelId,
  namespace: string,
  file: File,
  dryRun: boolean = true,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});

    dispatch(
      uploadFile({
        file,
        type: ContactsManagerFileStateTypesEnum.CONTACTS_CSV,
        panelId: contactsManagerPanel.id,
      }),
    );

    // If the file type is not a csv, do not upload and just show an error.
    if (!isValidCSVFileType(file)) {
      dispatch(
        uploadFailed({
          panelId: contactsManagerPanel.id,
          fileType: ContactsManagerFileStateTypesEnum.CONTACTS_CSV,
          errors: [
            {
              error: ContactsManagerCsvErrorsEnum.INVALID_FILE_TYPE,
            },
          ],
        }),
      );
      return;
    }

    try {
      // The endpoint doesn't support dry run so we do not try to upload during a dry run.
      if (!dryRun) {
        await dispatch(uploadContactsCsvAsyncAction(namespace, file));
      }
      dispatch(uploadComplete(contactsManagerPanel.id, dryRun));
    } catch (error) {
      const errorType = parseContactsCsvErrors(error);
      const errorFile = await maybeFetchContactCsvWithErrors(errorType, async () =>
        dispatch(uploadContactsCsvAsyncAction(namespace, file, true)),
      );
      dispatch(
        uploadFailed({
          panelId: contactsManagerPanel.id,
          fileType: ContactsManagerFileStateTypesEnum.CONTACTS_CSV,
          errorFile,
          errors: [
            {
              error: errorType,
            },
          ],
        }),
      );
      throw error;
    }
  };
}

export function contactsManagerClearFileAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(clearFile(contactsManagerPanel.id));
  };
}

export function contactsManagerSetMappingAction(mapping: Record<string, number>): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(setMapping({panelId: contactsManagerPanel.id, mapping}));
  };
}

/*
 * Helpers.
 */

function isValidCSVFileType(file: File) {
  return file.name.toLowerCase().endsWith('.csv');
}

function parseContactsCsvErrors(
  error: HttpResponseError,
): ContactsManagerContactsCsvErrorsEnum | ContactsManagerCsvErrorsEnum {
  switch (error.reason) {
    case 'csv_row_error':
      return ContactsManagerContactsCsvErrorsEnum.CSV_ROW_ERROR;
    case 'csv_file_error': {
      // eslint-disable-next-line camelcase
      const {error_strings = []} = error.body as any;

      // eslint-disable-next-line camelcase
      if (error_strings.includes('Invalid column heading')) {
        return ContactsManagerCsvErrorsEnum.UNRECOGNIZED_HEADERS;
      }

      // eslint-disable-next-line camelcase
      if (error_strings.includes('Missing headings in first row')) {
        return ContactsManagerCsvErrorsEnum.NO_HEADER;
      }

      // eslint-disable-next-line camelcase
      if (error_strings.includes('Maximum rows 3000')) {
        return ContactsManagerCsvErrorsEnum.TOO_MANY_ROWS;
      }

      return ContactsManagerCsvErrorsEnum.UNKNOWN_ERROR;
    }
    default:
      return ContactsManagerCsvErrorsEnum.UNKNOWN_ERROR;
  }
}

async function maybeFetchContactCsvWithErrors(
  errorType: ContactsManagerContactsCsvErrorsEnum | ContactsManagerCsvErrorsEnum,
  onFetchErrorFile: AsyncHandler<File | undefined>,
) {
  if (errorType === ContactsManagerContactsCsvErrorsEnum.CSV_ROW_ERROR) {
    const errorFile = await onFetchErrorFile();
    return errorFile;
  }
  return undefined;
}

/*
 * Computes the new mapping based on the previous mapping.
 * If the new file header exists in the previous mapping we keep the mapping
 * Otherwise, we try to find an existing custom field that matches the header
 */

function computeNewMapping(
  newHeaders: string[],
  accountCustomFields: CustomFieldModel[],
  prevMapping: Record<string, number> = {},
) {
  const newMapping: Record<string, number> = {};
  const mappedHeaders = Object.keys(prevMapping);
  newHeaders.forEach((header) => {
    // check if the new header was previously mapped (case insensitive)
    const foundMappedHeader = mappedHeaders.find(
      (mappedHeader) => mappedHeader.toLowerCase() === header.toLowerCase(),
    );
    // if the header is already mapped, keep the mapping
    if (foundMappedHeader) {
      newMapping[header] = prevMapping[foundMappedHeader];
      return;
    }

    // try to find a custom field with the same name
    const exactMatchCustomField = accountCustomFields.find((customField) => customField.name === header);

    if (exactMatchCustomField) {
      newMapping[header] = exactMatchCustomField.id;
      return;
    }

    // try to find a custom field with the same case-insensitive name
    const matchingCustomField = accountCustomFields.find(
      (customField) => customField.name.toLowerCase() === header.toLowerCase(),
    );

    if (matchingCustomField) {
      newMapping[header] = matchingCustomField.id;
    }
  });
  return newMapping;
}
